import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { auth } from '../../firebase';
import { apiUrl } from '../../utils/apiBase';

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' }
};

/** Kompakt alanlar: ~40px (size="small") */
const compactFieldSx = {
  ...inputSx,
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fff',
    minHeight: 40
  },
  '& .MuiInputBase-input': { fontSize: '0.875rem' }
};

const sectionPaperSx = {
  p: 2,
  borderRadius: 2,
  border: '1px solid #e8ecf1',
  bgcolor: '#fff',
  mb: 2.5
};

function IkasSettingsDialog({ open, onClose }) {
  const [brandId, setBrandId] = useState('');
  const [webSalesChannelId, setWebSalesChannelId] = useState('');
  const [priceListId, setPriceListId] = useState('');
  /** İkas `saveProductStockLocations` için depo kimliği (integrations/ikas.stockLocationId) */
  const [stockLocationId, setStockLocationId] = useState('');
  const [stockLocations, setStockLocations] = useState([]);
  const [stockLocLoading, setStockLocLoading] = useState(false);
  /** Firestore integrations/ikas içindeki categoryMap; UI düzenlenmez, kayıtta aynen korunur. */
  const [preservedCategoryMap, setPreservedCategoryMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [ikasCategories, setIkasCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryParentId, setEditCategoryParentId] = useState('');
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getBackendAuthHeaders = async () => {
    const headers = {};
    const sharedSecret = String(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET || '').trim();
    if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
    try {
      const token = await auth.currentUser?.getIdToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (e) {
      // no-op
    }
    return headers;
  };

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const url = apiUrl('/api/ikas/config');
      const response = await fetch(url, {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });
      const body = await response.json();
      if (!response.ok || !body?.success) {
        throw new Error(body?.message || body?.error || 'İkas ayarları alınamadı');
      }
      const data = body?.config && typeof body.config === 'object' ? body.config : {};

      setBrandId(String(data?.brandId || data?.brandMap?.['UKBA GOLD'] || '').trim());
      setWebSalesChannelId(
        String(data?.webSalesChannelId || data?.salesChannelMap?.web || '').trim()
      );
      setPriceListId(String(data?.priceListMap?.defaultSell || '').trim());
      setStockLocationId(String(data?.stockLocationId || '').trim());

      const rawCm = data?.categoryMap;
      setPreservedCategoryMap(rawCm && typeof rawCm === 'object' ? { ...rawCm } : {});
    } catch (e) {
      setError(`İkas ayarları okunamadı: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const loadIkasCategories = async (opts = {}) => {
    const silent = !!opts.silent;
    setCategoryLoading(true);
    if (!silent) {
      setError('');
      setSuccess('');
    }
    try {
      const url = apiUrl('/api/ikas/categories');
      const response = await fetch(url, {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || 'İkas kategori listesi alınamadı');
      }
      const list = Array.isArray(data?.categories) ? data.categories : [];
      setIkasCategories(list);
      if (!silent) {
        setSuccess(`İkas kategorileri yüklendi (${list.length})`);
      }
    } catch (e) {
      setError(`İkas kategorileri alınamadı: ${e?.message || e}`);
    } finally {
      setCategoryLoading(false);
    }
  };

  const loadStockLocations = async () => {
    setStockLocLoading(true);
    try {
      const url = apiUrl('/api/ikas/stock-locations');
      const response = await fetch(url, {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || 'Stok depoları alınamadı');
      }
      const list = Array.isArray(data?.locations) ? data.locations : [];
      setStockLocations(list);
    } catch (e) {
      setStockLocations([]);
    } finally {
      setStockLocLoading(false);
    }
  };

  const handleCreateIkasCategory = async () => {
    const name = String(newCategoryName || '').trim();
    if (!name) {
      setError('Kategori adı girin.');
      setSuccess('');
      return;
    }
    setCreatingCategory(true);
    setError('');
    setSuccess('');
    try {
      const url = apiUrl('/api/ikas/categories');
      const parentId =
        newCategoryParentId && String(newCategoryParentId).trim()
          ? String(newCategoryParentId).trim()
          : null;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getBackendAuthHeaders())
        },
        body: JSON.stringify({ name, parentId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(
          data?.detail ||
            data?.message ||
            data?.error ||
            data?.hint ||
            'Kategori oluşturulamadı'
        );
      }
      const created = data.category || {};
      setNewCategoryName('');
      setNewCategoryParentId('');
      setSuccess(
        `Kategori oluşturuldu: ${created.name || name}${created.id ? ` (${created.id})` : ''}`
      );
      await loadIkasCategories({ silent: true });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setCreatingCategory(false);
    }
  };

  const openEditCategory = (cat) => {
    if (!cat?.id) return;
    setError('');
    setEditCategoryId(String(cat.id));
    setEditCategoryName(String(cat.name || ''));
    setEditCategoryParentId(cat.parentId ? String(cat.parentId) : '');
    setEditCategoryOpen(true);
  };

  const handleUpdateIkasCategory = async () => {
    const name = String(editCategoryName || '').trim();
    const id = String(editCategoryId || '').trim();
    if (!id || !name) {
      setError('Kategori adı ve kayıt gerekli.');
      return;
    }
    setUpdatingCategory(true);
    setError('');
    setSuccess('');
    try {
      const parentId =
        editCategoryParentId && String(editCategoryParentId).trim()
          ? String(editCategoryParentId).trim()
          : null;
      const url = apiUrl('/api/ikas/categories/update');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getBackendAuthHeaders())
        },
        body: JSON.stringify({ id, name, parentId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(
          data?.detail ||
            data?.message ||
            data?.error ||
            data?.hint ||
            'Kategori güncellenemedi'
        );
      }
      const updated = data.category || {};
      setSuccess(
        `Kategori güncellendi: ${updated.name || name}${updated.id ? ` (${updated.id})` : ''}`
      );
      setEditCategoryOpen(false);
      await loadIkasCategories({ silent: true });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setUpdatingCategory(false);
    }
  };

  const handleDeleteIkasCategory = async (cat) => {
    if (!cat?.id) return;
    const ok = window.confirm('Bu kategoriyi silmek istediğine emin misin?');
    if (!ok) return;
    setDeletingCategoryId(cat.id);
    setError('');
    setSuccess('');
    try {
      const url = apiUrl(`/api/ikas/categories/${encodeURIComponent(cat.id)}`);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: await getBackendAuthHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(
          data?.detail ||
            data?.message ||
            data?.error ||
            data?.hint ||
            'Kategori silinemedi'
        );
      }
      setSuccess(`Kategori silindi: ${cat.name} (${cat.id})`);
      await loadIkasCategories({ silent: true });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setDeletingCategoryId(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadConfig();
    loadIkasCategories({ silent: true });
    loadStockLocations();
  }, [open]);

  const status = useMemo(
    () => ({
      hasBrand: !!String(brandId || '').trim(),
      hasWebSalesChannel: !!String(webSalesChannelId || '').trim(),
      hasStockLocation: !!String(stockLocationId || '').trim()
    }),
    [brandId, webSalesChannelId, stockLocationId]
  );

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const url = apiUrl('/api/ikas/config');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getBackendAuthHeaders())
        },
        body: JSON.stringify({
          brandId: String(brandId || '').trim(),
          webSalesChannelId: String(webSalesChannelId || '').trim(),
          stockLocationId: String(stockLocationId || '').trim(),
          brandMap: {
            'UKBA GOLD': String(brandId || '').trim()
          },
          salesChannelMap: {
            web: String(webSalesChannelId || '').trim()
          },
          priceListMap: {
            defaultSell: String(priceListId || '').trim()
          },
          categoryMap: preservedCategoryMap
        })
      });
      const text = await response.text();
      let body = {};
      try {
        body = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(
          `Sunucu yanıtı geçersiz (HTTP ${response.status}). Sayfayı yenileyip tekrar deneyin veya deploy sonrası birkaç dakika bekleyin.`
        );
      }
      if (!response.ok || !body?.success) {
        throw new Error(body?.message || body?.error || 'İkas ayarları kaydedilemedi');
      }

      setSuccess('İkas ayarları kaydedildi.');
    } catch (e) {
      setError(`İkas ayarları kaydedilemedi: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: 'min(1260px, 96vw)',
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>İkas Ayarları</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f5f7fa', py: 2.5 }}>
        {loading ? (
          <Typography>Ayarlar yükleniyor...</Typography>
        ) : (
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Paper sx={sectionPaperSx} elevation={0}>
              <Typography sx={{ fontWeight: 800, mb: 1.25, fontSize: '0.95rem', color: '#1e293b' }}>
                Durum Kartı
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  Brand ID: <strong>{status.hasBrand ? 'Var' : 'Yok'}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  Web Sales Channel ID: <strong>{status.hasWebSalesChannel ? 'Var' : 'Yok'}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  Stok deposu: <strong>{status.hasStockLocation ? 'Seçili' : 'Yok'}</strong>
                </Typography>
              </Box>
            </Paper>

            <Paper sx={sectionPaperSx} elevation={0}>
              <Typography sx={{ fontWeight: 800, mb: 0.75, fontSize: '1rem', color: '#0f172a' }}>
                Temel İkas Ayarları
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 1.5, lineHeight: 1.45 }}>
                Marka sabit <strong>UKBA GOLD</strong>; web mağazası <strong>ukbagold</strong>. Aşağıdaki alanlar İkas’taki
                ilgili kayıtların kimlikleridir.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: '12px',
                  alignItems: 'start'
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Brand ID"
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  sx={compactFieldSx}
                  helperText="UKBA GOLD → İkas marka kaydı"
                  FormHelperTextProps={{ sx: { fontSize: '0.7rem', m: 0, mt: 0.35 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Web Sales Channel ID"
                  value={webSalesChannelId}
                  onChange={(e) => setWebSalesChannelId(e.target.value)}
                  sx={compactFieldSx}
                  helperText="ukbagold web kanalı → İkas satış kanalı"
                  FormHelperTextProps={{ sx: { fontSize: '0.7rem', m: 0, mt: 0.35 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Price List ID (Opsiyonel)"
                  value={priceListId}
                  onChange={(e) => setPriceListId(e.target.value)}
                  sx={compactFieldSx}
                />
              </Box>
              <Box sx={{ mt: 1.75 }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 1, lineHeight: 1.45 }}>
                  Ürün gönderiminde <strong>stok adedi</strong> (`saveProductStockLocations`) seçtiğiniz İkas deposuna
                  yazılır. Depo kaydı yoksa ürün oluşur ama <strong>stok İkas’a gitmez</strong>.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'flex-start' }}>
                  {stockLocations.length > 0 ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Stok deposu (İkas)"
                      value={stockLocationId}
                      onChange={(e) => setStockLocationId(e.target.value)}
                      sx={{ ...compactFieldSx, flex: '1 1 300px', minWidth: 220 }}
                      disabled={stockLocLoading}
                      helperText="Listeden seçin."
                      FormHelperTextProps={{ sx: { fontSize: '0.7rem', m: 0, mt: 0.35 } }}
                    >
                      <MenuItem value="">
                        <em>Seçilmedi</em>
                      </MenuItem>
                      {stockLocationId &&
                      stockLocations.every((l) => String(l.id) !== String(stockLocationId)) ? (
                        <MenuItem value={stockLocationId}>
                          Kayıtlı depo (liste dışı): {stockLocationId}
                        </MenuItem>
                      ) : null}
                      {stockLocations.map((loc) => (
                        <MenuItem key={loc.id} value={loc.id}>
                          {loc.name || loc.id} — {loc.id}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="Stok deposu UUID (İkas)"
                      value={stockLocationId}
                      onChange={(e) => setStockLocationId(e.target.value.trim())}
                      sx={{ ...compactFieldSx, flex: '1 1 300px', minWidth: 220 }}
                      disabled={stockLocLoading}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      helperText="Liste boşsa İkas panelinden depo kimliğini yapıştırın; «Depoları yenile» deneyin."
                      FormHelperTextProps={{ sx: { fontSize: '0.7rem', m: 0, mt: 0.35 } }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => loadStockLocations()}
                    disabled={stockLocLoading}
                    sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5, minHeight: 40 }}
                  >
                    {stockLocLoading ? 'Yükleniyor...' : 'Depoları yenile'}
                  </Button>
                </Box>
              </Box>
            </Paper>

            <Paper
              sx={{ ...sectionPaperSx, maxWidth: { md: 'min(920px, 100%)' } }}
              elevation={0}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.75, fontSize: '0.95rem', color: '#0f172a' }}>
                Yeni İkas Kategorisi Ekle
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', lineHeight: 1.45 }}>
                Kategori adı ve isteğe bağlı üst kategori. Üst kategori için önce aşağıdan listeyi yenileyin.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '2fr 2fr 1fr' },
                  gap: '10px',
                  alignItems: 'stretch'
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Kategori adı"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Örn. BİLEZİK"
                  sx={compactFieldSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Üst kategori (opsiyonel)"
                  value={newCategoryParentId}
                  onChange={(e) => setNewCategoryParentId(e.target.value)}
                  sx={compactFieldSx}
                >
                  <MenuItem value="">Yok (kök kategori)</MenuItem>
                  {ikasCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                      {cat.path ? ` · ${cat.path}` : ''} — {cat.id}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleCreateIkasCategory}
                  disabled={creatingCategory || !String(newCategoryName || '').trim()}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 40,
                    alignSelf: 'stretch'
                  }}
                >
                  {creatingCategory ? 'Ekleniyor...' : 'Kategori Ekle'}
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ ...sectionPaperSx, mb: 0 }} elevation={0}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 2
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>
                  İkas Kategorileri
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadIkasCategories}
                    disabled={categoryLoading}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {categoryLoading ? 'Yükleniyor...' : 'Yenile / Listele'}
                  </Button>
                  <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                    {ikasCategories.length} kategori
                  </Typography>
                </Box>
              </Box>
              {ikasCategories.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  Henüz kategori listesi yüklenmedi.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)'
                    },
                    gap: '12px',
                    maxHeight: { md: 'min(52vh, 420px)' },
                    overflowY: 'auto',
                    pr: 0.5,
                    pb: 0.5
                  }}
                >
                  {ikasCategories.map((cat) => (
                    <Box
                      key={cat.id}
                      sx={{
                        position: 'relative',
                        minHeight: 110,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        p: '12px',
                        borderRadius: '8px',
                        border: '1px solid #eee',
                        bgcolor: '#fff',
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}
                      >
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            aria-label="Düzenle"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditCategory(cat);
                            }}
                            sx={{ p: 0.35 }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Sil"
                              color="error"
                              disabled={deletingCategoryId === cat.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteIkasCategory(cat);
                              }}
                              sx={{ p: 0.35 }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Typography
                        onClick={() => openEditCategory(cat)}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          color: '#0f172a',
                          pr: 5,
                          cursor: 'pointer',
                          lineHeight: 1.35
                        }}
                      >
                        {cat.name}
                      </Typography>
                      <Box
                        onClick={() => openEditCategory(cat)}
                        sx={{ cursor: 'pointer', minWidth: 0 }}
                      >
                        <Typography
                          display="block"
                          sx={{
                            fontSize: '12px',
                            color: '#777',
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                          }}
                        >
                          ID: {cat.id}
                        </Typography>
                        <Typography
                          display="block"
                          sx={{
                            fontSize: '12px',
                            color: '#777',
                            lineHeight: 1.4
                          }}
                        >
                          Parent: {cat.parentId || '—'}
                        </Typography>
                        <Typography
                          display="block"
                          sx={{
                            fontSize: '12px',
                            color: '#777',
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                          }}
                        >
                          Path: {cat.path || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Kapat
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading} sx={{ textTransform: 'none', fontWeight: 800 }}>
          {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>

      <Dialog
        open={editCategoryOpen}
        onClose={() => !updatingCategory && setEditCategoryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>İkas Kategorisini Düzenle</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Kategori adı"
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              required
              sx={inputSx}
            />
            <TextField
              fullWidth
              select
              label="Üst kategori (opsiyonel)"
              value={editCategoryParentId}
              onChange={(e) => setEditCategoryParentId(e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">Yok (kök kategori)</MenuItem>
              {ikasCategories
                .filter((c) => c.id !== editCategoryId)
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                    {c.path ? ` · ${c.path}` : ''} — {c.id}
                  </MenuItem>
                ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              ID: {editCategoryId}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={() => setEditCategoryOpen(false)}
            disabled={updatingCategory}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            İptal
          </Button>
          <Button
            onClick={handleUpdateIkasCategory}
            variant="contained"
            disabled={updatingCategory || !String(editCategoryName || '').trim()}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {updatingCategory ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default IkasSettingsDialog;
